/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { format } from "date-fns";
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { MdLocationPin } from "react-icons/md";
import useUserContext from "../../hooks/useUserContext";
import notify from "../../services/toast";
import { MAGIC_NUMBERS, APP_LOCATIONS } from "../../constant";
import getApplicants from "./requests/getApplicantsSearch";
import getImpactStats from "../impact_dashboard/dashboard/getImpactStats";
import getImpactStatsByMarket from "./requests/getImpactStatsByMarket";
import deleteApplication from "./requests/deleteApplication";
import Modal from "../../components/modal/Modal";
import cpLogo from "../../assets/CommunityPartnerLogo.png";

ChartJS.register(ArcElement, Tooltip, Legend);

const customTheme = (theme) => ({
	...theme,
	borderRadius: 5,
	colors: {
		...theme.colors,
		primary25: "#ecf8ef",
		primary: "#56c271",
	},
});

const ScholarshipApplications = () => {
	const { city } = useParams();
	const { userState } = useUserContext();

	const [applications, setApplications] = useState([]);
	const [stats, setStats] = useState({});
	const [availableYears, setAvailableYears] = useState([]);
	const [selectedYear, setSelectedYear] = useState("All");
	const [locationFilter, setLocationFilter] = useState("");
	const [typeFilter, setTypeFilter] = useState("All");
	const [appStatusFilter, setAppStatusFilter] = useState("All");
	const [cmStatusFilter, setCmStatusFilter] = useState("All");

	const [pagination, setPagination] = useState({ page: 0, limit: 10 });
	const [currentId, setCurrentId] = useState(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const CITY_DISPLAY_NAME_MAP = {
		tampa_bay: "Central Florida", // override route param display
	};

	const getAllApplications = async () => {
		const res = await getApplicants({ keyword: "", limit: 10000, city }, userState.accessToken);
		if (res.status === "success") {
			const sorted = res.data.results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
			setApplications(sorted);

			const years = Array.from(new Set(sorted.map(app => new Date(app.createdAt).getFullYear())))
				.filter(y => y !== 1969)
				.sort((a, b) => b - a);
			setAvailableYears(["All", ...years]);
		} else {
			notify("Failed to load data", "error");
		}
	};

	const fetchStats = async (year = null, loc = "") => {
		const statFetcher = loc ? getImpactStatsByMarket : getImpactStats;
		const res = await statFetcher(loc || city, userState.accessToken, year !== "All" ? year : null);

		if (res.status === "success") {
			setStats(res.data);
		} else {
			let msg = Array.isArray(res.message) ? res.message[0].message : res.message;
			if (MAGIC_NUMBERS.SERVER_ERROR_CODES.includes(res.statusCode)) {
				msg = "Server error. Please try again later.";
			}
			notify(msg, "error");
		}
	};

	const confirmDelete = (id) => {
		setCurrentId(id);
		setIsDeleteModalOpen(true);
	};

	const handleDelete = async () => {
		const res = await deleteApplication(currentId, userState.accessToken);
		if (res.status === "success") {
			notify("Application deleted", "info");
			await getAllApplications();
		} else {
			notify("Failed to delete", "error");
		}
		setIsDeleteModalOpen(false);
		setCurrentId(null);
	};

	useEffect(() => {
		getAllApplications();
	}, []);

	useEffect(() => {
		fetchStats(selectedYear, locationFilter);
	}, [selectedYear, locationFilter]);

	const filteredApplications = applications
		.filter(app =>
			(selectedYear === "All" || new Date(app.createdAt).getFullYear() === +selectedYear) &&
			(locationFilter === "" || app.coLocation === locationFilter) &&
			(typeFilter === "All" || app.type?.toLowerCase() === typeFilter.toLowerCase()) &&
			(appStatusFilter === "All" || app.appStatus?.toLowerCase() === appStatusFilter.toLowerCase()) &&
			(cmStatusFilter === "All" || app.cmStatus?.toLowerCase() === cmStatusFilter.toLowerCase())
		);

	const currentPageData = filteredApplications.slice(
		pagination.page * pagination.limit,
		(pagination.page + 1) * pagination.limit
	);

	const pieChartData = {
		labels: ['Approved', 'Pending', 'Denied', 'Boost', 'Give'],
		datasets: [{
			data: [
				stats.totalApprovedApplications,
				stats.totalPendingApplications,
				stats.totalDeniedApplications,
				stats.totalBoostApplications,
				stats.totalGiveApplications,
			],
			backgroundColor: ['#56c271', '#003057', '#D32F2F', '#b2c7c9', '#f66a47'],
		}]
	};

	const locations = city ? APP_LOCATIONS[city]?.locations || [] : [];

	return (

		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			<h1 className="text-2xl font-bold my-6">
				{CITY_DISPLAY_NAME_MAP[city] || city.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())} Impact Scholarships
			</h1>


			{/* Year Filter */}
			<div className="my-6 flex flex-wrap gap-2">
				<span className="text-lg font-semibold">Filter by Year:</span>
				{availableYears.map(year => (
					<button
						key={year}
						onClick={() => setSelectedYear(year)}
						className={`px-4 py-2 rounded-md text-sm font-medium ${selectedYear === year ? "bg-green-600 text-white" : "bg-gray-200 hover:bg-green-500"
							}`}
					>
						{year}
					</button>
				))}
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{/* Stats Grid - 2 Columns, 3 Rows */}
				<div className="col-span-1 md:col-span-2 lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">

					{[
						{ label: "Scholarship Dollars Awarded", value: `$${stats?.annualAward?.[0]?.annualAward?.toLocaleString() || 0}` },
						{ label: "Approved Scholars", value: stats.totalApprovedApplications },
						{ label: "Pending Scholars", value: stats.totalPendingApplications },
						{ label: "Denied Scholars", value: stats.totalDeniedApplications },
						{ label: "Boost Scholars", value: stats.totalBoostApplications },
						{ label: "Give Scholars", value: stats.totalGiveApplications },
					].map((item, i) => (
						<div key={i} className="bg-white p-4 rounded shadow">
							<div className="text-4xl font-bold">{item.value || 0}</div>
							<div className="text-gray-600">{item.label}</div>
						</div>
					))}
				</div>

				{/* Pie Chart in 3rd Column */}
				<div className="col-span-1 lg:col-span-1 mt-8 lg:mt-0">
					<div className="col-span-full lg:col-span-1">
						<div className="bg-white p-4 rounded shadow">
							<h2 className="text-lg font-semibold mb-2">Application Breakdown</h2>
							<Pie data={pieChartData} />
						</div>
					</div>
				</div>
			</div>

			{/* Location Tabs */}
			<div className="mt-12 flex flex-wrap gap-2">
				<button
					onClick={() => setLocationFilter("")}
					className={`px-4 py-2 rounded-full text-sm font-medium ${!locationFilter ? "bg-green-500 text-white" : "bg-gray-100"
						}`}
				>
					All
				</button>
				{locations.map(loc => (
					<button
						key={loc.value}
						onClick={() => setLocationFilter(loc.label)}
						className={`px-4 py-2 rounded-full text-sm font-medium ${locationFilter === loc.label ? "bg-green-500 text-white" : "bg-gray-100"
							}`}
					>
						{loc.label}
					</button>
				))}
			</div>

			{/* Filters */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
				{[
					{ label: "Application Type", value: typeFilter, setter: setTypeFilter, options: ["All", "Community Partner", "give", "boost"] },
					{ label: "Application Status", value: appStatusFilter, setter: setAppStatusFilter, options: ["All", "pending", "approved", "rejected"] },
					{ label: "CM Status", value: cmStatusFilter, setter: setCmStatusFilter, options: ["All", "pending", "approved", "rejected"] },
				].map(({ label, value, setter, options }) => (
					<div key={label}>
						<label className="block text-sm font-medium mb-1">{label}</label>
						<Select
							value={{ label: value, value }}
							options={options.map(opt => ({ label: opt, value: opt }))}
							onChange={opt => setter(opt.value)}
							theme={customTheme}
						/>
					</div>
				))}
			</div>

			{/* Applications Table */}
			<div className="mt-8 overflow-x-auto">
				<table className="min-w-full bg-white rounded shadow border">
					<thead className="bg-gray-100 text-gray-700 text-sm">
						<tr>
							<th className="p-3">Organization</th>
							<th className="p-3">Type</th>
							<th className="p-3">App Status</th>
							<th className="p-3">CM Status</th>
							<th className="p-3">Date</th>
							<th className="p-3">Location</th>
							<th className="p-3">Actions</th>
						</tr>
					</thead>
					<tbody>
						{currentPageData.map(app => (
							<tr key={app._id} className="border-t">
								<td className="p-3 flex items-center space-x-3">
									<img
										src={
											app.type === "Community Partner"
												? cpLogo
												: app.logoImage?.small?.url || app.logoImage?.logoImage?.url
										}
										alt={app.orgName}
										className="w-10 h-10 rounded-full object-cover"
									/>
									<a href={`/dashboard/${app.type === "Community Partner" ? "community-partner" : "profile"}/${app._id}`} target="_blank" rel="noreferrer">
										{app.orgName}
									</a>
								</td>
								<td className="p-3 capitalize">{app.type}</td>
								<td className="p-3 capitalize text-sm font-semibold text-green-600">{app.appStatus}</td>
								<td className="p-3 capitalize text-sm font-semibold text-success-800">{app.cmStatus}</td>
								<td className="p-3">{format(new Date(app.createdAt), "MM/dd/yyyy")}</td>
								<td className="p-3 flex items-center">
									<MdLocationPin className="mr-1" />
									{app.coLocation || "Unknown"}
								</td>
								<td className="p-3">
									<button onClick={() => confirmDelete(app._id)} className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600">
										Delete
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			<div className="mt-4 flex justify-between items-center">
				<span className="text-sm text-gray-600">
					Page {pagination.page + 1} of {Math.ceil(filteredApplications.length / pagination.limit)}
				</span>
				<div>
					<button
						disabled={pagination.page === 0}
						onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
						className="px-4 py-2 bg-gray-200 rounded mr-2"
					>
						Prev
					</button>
					<button
						disabled={(pagination.page + 1) * pagination.limit >= filteredApplications.length}
						onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
						className="px-4 py-2 bg-gray-200 rounded"
					>
						Next
					</button>
				</div>
			</div>

			{/* Delete Confirmation Modal */}
			{isDeleteModalOpen && (
				<Modal>
					<div className="p-6 bg-white">
						<h2 className="text-lg font-bold ">Confirm Deletion</h2>
						<p className="mt-2">Are you sure you want to delete this application? This action cannot be undone.</p>
						<div className="mt-4 flex justify-end">
							<button
								className="px-4 py-2 bg-gray-300 rounded-md mr-2"
								onClick={() => setIsDeleteModalOpen(false)}
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 bg-red-500 text-white rounded-md"
								onClick={handleDelete}
							>
								Confirm
							</button>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default ScholarshipApplications;
